import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField, Button, Typography, Alert } from "@mui/material";
import { getComparator, handleSort } from "../../hooks/useTable.js";
import {
	SCHEDULE_ERRORS,
	SCHEDULE_MESSAGES,
} from "../../common/constants/scheduleModule/scheduleConstants.js";
import {
	SIZES,
	VARIANTS,
	AUTO_COMPLETE,
	SORT_ORDERS,
} from "../../common/constants/commonConstants.js";
import {
	SCHEDULE_STRINGS,
	SCHEDULE_TABLE_COLUMNS,
	SCHEDULE_KEYS,
} from "../../common/constants/scheduleModule/scheduleConstants.js";
import {
	getupcomingScheduleInfo,
	calibrateScheduleMessage,
	stopAllSchedulesMessage,
} from "../../services/scheduleModule/scheduledataServices.js";
import UpcomoingSchedulesTable from "./UpcomoingSchedulesTable.jsx";
import DialogComponent from "../../common/components/DialogComponent.jsx";
import ProgressOverlay from "../../common/components/ProgressOverlay.jsx";
import RefreshCountdonwn from "../campaignModule/refreshCountdonwn.jsx";

const REFRESH_INTERVAL = 5;

const UpcomingSchedules = () => {
	const [page, setPage] = useState(0);
	const [schedules, setSchedules] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [showSearch, setShowSearch] = useState(true);
	const [searchSchedule, setSearchSchedule] = useState("");
	const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
	const [orderBy, setOrderBy] = useState(SCHEDULE_KEYS.SCHEDULED_AT);
	// Initial load shows the spinner; background refreshes don't, to avoid flicker
	const [loadingSchedules, setLoadingSchedules] = useState(true);
	const [openCalibrateDialog, setOpenCalibrateDialog] = useState(false);
	const [openStopAllDialog, setOpenStopAllDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [resetSignal, setResetSignal] = useState(0);
	const userId = localStorage.getItem("userId") || "";

	// Initial load + 15s auto-refresh — same pattern as Processes
	useEffect(() => {
		const fetchSchedules = async (showLoader = false) => {
			if (showLoader) setLoadingSchedules(true);
			try {
				const scheduleData = await getupcomingScheduleInfo();
				setSchedules(scheduleData || []);
			} catch (error) {
				console.error(SCHEDULE_ERRORS.FETCH_SCHEDULES_ERROR, error);
			} finally {
				if (showLoader) setLoadingSchedules(false);
			}
		};

		fetchSchedules(true);

		const intervalId = setInterval(() => {
			fetchSchedules(false);
			setResetSignal((prev) => prev + 1);
		}, REFRESH_INTERVAL * 1000);

		return () => clearInterval(intervalId);
	}, []);

	useEffect(() => {
		setPage(0);
	}, [searchSchedule]);

	// Manual fetch used by action flows — shows the loader and resets countdown
	const refetchWithLoader = async () => {
		try {
			setLoadingSchedules(true);
			const scheduleData = await getupcomingScheduleInfo();
			setSchedules(scheduleData || []);
			setResetSignal((prev) => prev + 1);
		} catch (error) {
			console.error(SCHEDULE_ERRORS.FETCH_SCHEDULES_ERROR, error);
		} finally {
			setLoadingSchedules(false);
		}
	};

	const { schedulesToDisplay, filteredSchedulesLength } = useMemo(() => {
		const search = (searchSchedule || "").toLowerCase().trim();

		const filteredSchedules = schedules.filter((s) => {
			if (!search) return true;
			return (
				(s.campaignname || "").toLowerCase().includes(search) ||
				(s.CampaignScheduleName || "").toLowerCase().includes(search)
			);
		});

		// Map your column ids (orderBy) to the actual fields on the schedule row
		const SORT_FIELD_MAP = {
			SCHEDULED_AT: "scheduled_at",
			CAMPAIGN_NAME: "campaignname",
			MESSAGE_COUNT: "messageCount",
			SCHEDULE_NAME: "CampaignScheduleName",
			CHANNEL: "channeltypename",
		};

		const field = SORT_FIELD_MAP[orderBy] ?? orderBy;

		const toTime = (v) => {
			const t = new Date(v).getTime();
			return Number.isFinite(t) ? t : -Infinity;
		};

		const sorted = [...filteredSchedules].sort((a, b) => {
			const aVal = a?.[field];
			const bVal = b?.[field];

			// Date sort for scheduled_at
			if (field === "scheduled_at") {
				const diff = toTime(aVal) - toTime(bVal);
				return order === SORT_ORDERS.ASCENDING ? diff : -diff;
			}

			// Numeric sort when possible
			const aNum = Number(aVal);
			const bNum = Number(bVal);
			const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum);
			if (bothNumeric) {
				const diff = aNum - bNum;
				return order === SORT_ORDERS.ASCENDING ? diff : -diff;
			}

			// String fallback
			const aStr = (aVal ?? "").toString();
			const bStr = (bVal ?? "").toString();
			const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
			return order === SORT_ORDERS.ASCENDING ? cmp : -cmp;
		});

		return {
			schedulesToDisplay: sorted.slice(
				page * rowsPerPage,
				page * rowsPerPage + rowsPerPage
			),
			filteredSchedulesLength: filteredSchedules.length,
		};
	}, [schedules, searchSchedule, order, orderBy, page, rowsPerPage]);

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	const handleCalibrateConfirm = async () => {
		try {
			setLoading(true);
			setOpenCalibrateDialog(false);
			await calibrateScheduleMessage(Number(userId));
			setSuccessMessage(SCHEDULE_MESSAGES.CALIBRATE_SUCCESS);
			await refetchWithLoader();
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error(SCHEDULE_MESSAGES.CALIBRATE_FAILURE, err);
			setErrorMessage(SCHEDULE_MESSAGES.CALIBRATE_FAILURE);
			setTimeout(() => setErrorMessage(""), 3000);
		} finally {
			setLoading(false);
			setOpenCalibrateDialog(false);
		}
	};

	const handleStopAllConfirm = async () => {
		try {
			setLoading(true);
			setOpenStopAllDialog(false);
			await stopAllSchedulesMessage(Number(userId));
			setSuccessMessage(SCHEDULE_MESSAGES.STOP_ALL_SCHEDULES_SUCCESS);
			await refetchWithLoader();
			setTimeout(() => setSuccessMessage(""), 3000);
		} catch (err) {
			console.error(SCHEDULE_MESSAGES.STOP_ALL_SCHEDULES_FAILURE, err);
			setErrorMessage(SCHEDULE_MESSAGES.STOP_ALL_SCHEDULES_FAILURE);
			setTimeout(() => setErrorMessage(""), 3000);
		} finally {
			setLoading(false);
			setOpenStopAllDialog(false);
		}
	};

	return (
		<>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}
			{loading && <ProgressOverlay />}

			<RefreshCountdonwn refreshInterval={REFRESH_INTERVAL} resetSignal={resetSignal} />

			{showSearch && (
				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid
						size={SIZES.GROW}
						sx={{ display: "flex", alignItems: "center" }}
					>
						<TextField
							fullWidth
							size={SIZES.SMALL}
							value={searchSchedule}
							variant={VARIANTS.OUTLINED}
							autoComplete={AUTO_COMPLETE.OFF}
							label={SCHEDULE_STRINGS.SEARCH_SCHEDULES}
							onChange={(e) => setSearchSchedule(e.target.value)}
						/>
						<Button
							variant="contained"
							color="error"
							sx={{ ml: 2, whiteSpace: "nowrap" }}
							onClick={() => setOpenStopAllDialog(true)}
						>
							{SCHEDULE_STRINGS.STOP_ALL_SCHEDULES}
						</Button>
						<Button
							variant="contained"
							sx={{ ml: 2, whiteSpace: "nowrap" }}
							onClick={() => setOpenCalibrateDialog(true)}
						>
							{SCHEDULE_STRINGS.RESET_ALL_SCHEDULES}
						</Button>
					</Grid>
				</Grid>
			)}
			<UpcomoingSchedulesTable
				page={page}
				order={order}
				setPage={setPage}
				orderBy={orderBy}
				rowsPerPage={rowsPerPage}
				setShowSearch={setShowSearch}
				schedules={schedulesToDisplay}
				setRowsPerPage={setRowsPerPage}
				columns={SCHEDULE_TABLE_COLUMNS}
				handleRequestSort={handleRequestSort}
				isSchedulesLoading={loadingSchedules}
				filteredSchedulesLength={filteredSchedulesLength}
			/>

			{/* Reset All Schedules (Calibrate) confirmation */}
			<DialogComponent
				open={openCalibrateDialog}
				onClose={() => setOpenCalibrateDialog(false)}
				title={SCHEDULE_STRINGS.CALIBRATE_CONFIRMATION_TITLE}
				actions={
					<Button
						variant="contained"
						onClick={handleCalibrateConfirm}
					>
						{SCHEDULE_STRINGS.CONFIRM}
					</Button>
				}
			>
				<Typography>{SCHEDULE_MESSAGES.CALIBRATE_CONFIRMATION}</Typography>
			</DialogComponent>

			{/* Stop All Schedules confirmation */}
			<DialogComponent
				open={openStopAllDialog}
				onClose={() => setOpenStopAllDialog(false)}
				title={SCHEDULE_STRINGS.STOP_ALL_SCHEDULES_CONFIRMATION_TITLE}
				actions={
					<Button
						variant="contained"
						color="error"
						onClick={handleStopAllConfirm}
					>
						{SCHEDULE_STRINGS.CONFIRM}
					</Button>
				}
			>
				<Typography>{SCHEDULE_MESSAGES.STOP_ALL_SCHEDULES_CONFIRMATION}</Typography>
			</DialogComponent>
		</>
	);
};

export default UpcomingSchedules;